import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Monitor, Smartphone } from 'lucide-react';

import { tx } from './i18n';

// The product's real page, beside the fields it is written in.
//
// The storefront draws the page in a frame and says which field each part
// comes from (its src/site/editBridge.js): pointing at a part opens its field
// here, the part of the field being written is shown there, and what is typed
// shows on the page before it is saved. Nothing is saved through the frame.
//
// The storefront this portal shows: the one on this machine when the portal
// runs here, the storefront's own address otherwise, or the one the build
// names. The shop's domain (www.phenomelongevity.com) is not it yet: that is
// still the old shop, which refuses to be shown in a frame.
const LOCAL = ['localhost', '127.0.0.1'];
export const SHOP_HOST = 'pllandingpage.vercel.app';
export function storefrontOrigin() {
  const named = String(process.env.REACT_APP_STOREFRONT_ORIGIN || '').replace(/\/+$/, '');
  if (named) return named;
  return LOCAL.includes(window.location.hostname) ? 'http://localhost:57322' : `https://${SHOP_HOST}`;
}

// The page as a phone shows it, at its real size, or as a computer does, made smaller to fit.
const VIEWS = { phone: { width: 390, scale: 1 }, computer: { width: 1280, scale: 0.42 } };
const READY_MS = 12000;

// `hash`: the part of the page it opens at; `title` and `hint`: the words over
// the frame where the page is only looked at (the Bundles page).
export default function ProductPageLive({
  path, focus, typed, saved, onPick, onPickPhoto, hash = '', title = '', hint = '',
}) {
  const frame = useRef(null);
  const origin = useRef(null);
  const [view, setView] = useState('phone');
  // 'waiting' until the page says it is drawn, 'ready', or 'silent' when it never does.
  const [state, setState] = useState('waiting');
  const base = storefrontOrigin();
  const picked = useRef(onPick);
  const pickedPhoto = useRef(onPickPhoto);
  picked.current = onPick;
  pickedPhoto.current = onPickPhoto;

  const tell = useCallback((message) => {
    if (origin.current && frame.current?.contentWindow) {
      frame.current.contentWindow.postMessage({ source: 'ph-portal', ...message }, origin.current);
    }
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.source !== frame.current?.contentWindow) return;
      const message = event.data;
      if (!message || message.source !== 'ph-storefront') return;
      if (message.type === 'ready') {
        origin.current = event.origin;
        setState('ready');
      } else if (message.type === 'pick' && typeof message.field === 'string') picked.current(message.field);
      else if (message.type === 'pick-photo') pickedPhoto.current();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // A page that never answers (a storefront without the preview) is shown as it is, and said so.
  useEffect(() => {
    if (state !== 'waiting') return undefined;
    const timer = window.setTimeout(() => setState((current) => (current === 'waiting' ? 'silent' : current)), READY_MS);
    return () => window.clearTimeout(timer);
  }, [state, path]);

  // The field being written: its part of the page, and its words as they are typed.
  useEffect(() => { if (state === 'ready' && focus) tell({ type: 'focus', field: focus }); }, [focus, state, tell]);
  useEffect(() => {
    if (state === 'ready' && typed) tell({ type: 'preview', field: typed.field, value: typed.value });
  }, [typed, state, tell]);
  // After a save the page reads its words again.
  const lastSaved = useRef(saved);
  useEffect(() => {
    if (saved === lastSaved.current) return;
    lastSaved.current = saved;
    if (state === 'ready') { setState('waiting'); tell({ type: 'reload' }); }
  }, [saved, state, tell]);

  if (!path) {
    return <aside className="xo-pm-live"><p className="xo-pm-live-note">{tx('ui.products.page.live.no_page')}</p></aside>;
  }
  const { width, scale } = VIEWS[view];
  return <aside className="xo-pm-live" aria-label={tx('ui.products.page.live.title')}>
    <div className="xo-pm-live-head">
      <span>
        <strong>{title || tx('ui.products.page.live.title')}</strong>
        <small>{state === 'silent' ? tx('ui.products.page.live.silent') : hint || tx('ui.products.page.live.hint')}</small>
      </span>
      <div className="xo-pm-live-views" role="radiogroup" aria-label={tx('ui.products.page.live.view')}>
        <button type="button" role="radio" aria-checked={view === 'phone'} className={view === 'phone' ? 'is-on' : ''}
          aria-label={tx('ui.products.page.live.phone')} title={tx('ui.products.page.live.phone')}
          onClick={() => setView('phone')}><Smartphone aria-hidden="true" /></button>
        <button type="button" role="radio" aria-checked={view === 'computer'} className={view === 'computer' ? 'is-on' : ''}
          aria-label={tx('ui.products.page.live.computer')} title={tx('ui.products.page.live.computer')}
          onClick={() => setView('computer')}><Monitor aria-hidden="true" /></button>
      </div>
    </div>
    <div className={`xo-pm-live-stage is-${view}`} style={{ '--live-width': `${width}px`, '--live-scale': scale }}>
      <iframe ref={frame} title={title || tx('ui.products.page.live.title')}
        src={`${base}${path}?ph-edit=1${hash ? `#${hash}` : ''}`}
        onLoad={() => setState((current) => (current === 'ready' ? current : 'waiting'))} />
    </div>
  </aside>;
}
